import type { LucideIcon } from "lucide-react";

import baratheonBanner from "@/assets/leaderboard/houses/baratheon-banner.webp";
import lannisterBanner from "@/assets/leaderboard/houses/lannister-banner.webp";
import starkBanner from "@/assets/leaderboard/houses/stark-banner.webp";
import targaryenBanner from "@/assets/leaderboard/houses/targaryen-banner.webp";
import tyrellBanner from "@/assets/leaderboard/houses/tyrell-banner.webp";
import { Column, Row, Text } from "@/components";
import {
  HexagonIconBadge,
  MetricLeadersHoverCard,
  TopPerformerLeaders,
} from "@/components/leaderboard";
import { Card, CardContent } from "@/components/ui/card";
import { leaderboardMetrics } from "@/lib/leaderboardMetrics";
import { cn } from "@/lib/utils";

import type {
  HexGradient,
  LeaderboardHouseMetric,
  LeaderboardUser,
} from "@/types";

const HOUSE_THEMES = {
  revenue: {
    background: tyrellBanner,
    shade: "from-emerald-950/80 via-black/20 to-transparent",
    badgeBackdrop: "#bbd6bc",
  },
  placements: {
    background: lannisterBanner,
    shade: "from-red-950/85 via-black/20 to-transparent",
    badgeBackdrop: "#e6cc96",
  },
  screens: {
    background: starkBanner,
    shade: "from-slate-950/85 via-black/20 to-transparent",
    badgeBackdrop: "#cbd5e1",
  },
  submittals: {
    background: targaryenBanner,
    shade: "from-black/90 via-black/25 to-transparent",
    badgeBackdrop: "#cfa6a6",
  },
  interviews: {
    background: baratheonBanner,
    shade: "from-stone-950/85 via-black/20 to-transparent",
    badgeBackdrop: "#d8be83",
  },
};

export const LeaderboardHouseCard = ({
  users,
  metricKey,
  label,
  icon,
  hexGradient,
  formatValue,
}: {
  users: LeaderboardUser[];
  metricKey: LeaderboardHouseMetric;
  label: string;
  icon: LucideIcon;
  hexGradient: HexGradient;
  formatValue?: (value: number) => string;
}) => {
  /* Helpers */
  const theme = HOUSE_THEMES[metricKey];
  const maxValue = Math.max(...users.map((user) => user[metricKey]), 0);
  const leaders = users
    .filter((user) => user[metricKey] === maxValue && maxValue > 0)
    .sort((a, b) => a.firstName.localeCompare(b.firstName));
  const formatMetric = (value: number) => {
    if (formatValue) return formatValue(value);
    return Math.ceil(value).toLocaleString("en-US");
  };

  /* Render */
  const render = () => {
    return (
      <Column className="relative min-w-0 pt-2">
        <Row className="pointer-events-none absolute inset-x-0 top-0 h-2 rounded-full border border-amber-100/20 bg-gradient-to-b from-stone-500 via-stone-700 to-stone-950 shadow-sm" />
        <Card
          className="relative flex min-h-64 flex-1 overflow-hidden rounded-none rounded-t-lg border-0 bg-slate-950 shadow-none"
          style={{
            clipPath: "polygon(0 0, 100% 0, 100% 94%, 50% 100%, 0 94%)",
          }}
        >
          <img
            src={theme.background}
            alt=""
            className="pointer-events-none absolute inset-0 h-full w-full object-cover object-top"
          />
          <Column
            className={cn(
              "pointer-events-none absolute inset-0 bg-gradient-to-r",
              theme.shade,
            )}
          />
          <Column className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/10 to-black/15" />
          <Column className="pointer-events-none absolute bottom-9 left-2 top-2 w-px bg-amber-100/20" />
          <Column className="pointer-events-none absolute bottom-9 right-2 top-2 w-px bg-amber-100/20" />
          <CardContent className="relative flex flex-1 flex-col gap-3 px-5 pb-8 pt-4">
            <Row className="items-center justify-between gap-2">
              <Text className="text-xs font-semibold uppercase leading-tight tracking-wider text-slate-100">
                {label}
              </Text>
              <MetricLeadersHoverCard
                title={label}
                icon={icon}
                gradient={hexGradient}
                leaders={leaderboardMetrics.topN(users, metricKey, 3)}
                valueOf={(user) => user[metricKey]}
                format={formatMetric}
                isDark
                badgeBackdropColor={theme.badgeBackdrop}
              >
                <Row className="cursor-default">
                  <HexagonIconBadge
                    icon={icon}
                    gradient={hexGradient}
                    size="table"
                    backdropColor={theme.badgeBackdrop}
                  />
                </Row>
              </MetricLeadersHoverCard>
            </Row>
            <Text className="text-5xl font-bold tabular-nums leading-none tracking-tight text-white drop-shadow-md">
              {maxValue > 0 ? formatMetric(maxValue) : "—"}
            </Text>
            <Column className="mt-auto pt-8">
              <TopPerformerLeaders leaders={leaders} isDark />
            </Column>
          </CardContent>
        </Card>
      </Column>
    );
  };

  return render();
};
